import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faSave } from "@fortawesome/pro-solid-svg-icons/faSave";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import SavedFilter, { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { Avatar } from "@mapmycustomers/ui";

import ListDrawerWithPagination from "@app/component/filtering/components/StandardFilterButton/components/Header/SavedFiltersDrawer/ListDrawerWithPagination";
import { getFavoriteFilters } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { deleteSavedFilter } from "@app/store/savedFilters/actions";
import Iam from "@app/types/Iam";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { formatDate } from "@app/util/formatters";

import styles from "./SavedFiltersDrawer.module.scss";

interface Props {
  editable?: boolean;
  favoriteFiltersIds?: MapSavedFilter["id"][];
  filters: MapSavedFilter[];
  information?: React.ReactNode;
  me?: Iam;
  onDelete?: (filter: SavedFilter) => void;
  onEdit?: (filter: MapSavedFilter) => void;
  onHide: () => void;
  onSelect?: (filter: MapSavedFilter) => void;
  showOverrideConfirmation?: boolean;
  visible?: boolean;
}

const messages = defineMessages({
  apply: {
    id: "savedFilters.pane.selectConfirmation.apply",
    defaultMessage: "Got it",
    description: "Saved filters pane select confirmation apply text",
  },
  deleteConfirmation: {
    id: "savedFilters.pane.deleteConfirmation",
    defaultMessage: "Are you sure you want to permanently delete this saved filter?",
    description: "Saved filters pane delete confirmation",
  },
  disabledTooltip: {
    id: "savedFilters.pane.disabledTooltip",
    defaultMessage:
      "Saved Filter cannot be applied because at least one rule is based on a field <b>you do not have access to</b>. Please delete this filter and construct again.",
    description: "Saved filters pane delete confirmation",
  },
  selectConfirmation: {
    id: "savedFilters.pane.selectConfirmation",
    defaultMessage: "Selecting this will override any filters currently applied.",
    description: "Saved filters pane select confirmation",
  },
  subTitle1: {
    id: "savedFilters.pane.row.subTitle1",
    defaultMessage: "Last Modified: {updatedAt}",
    description: "Saved filters pane row subtitle",
  },
  title: {
    id: "savedFilters.pane.title",
    defaultMessage: "Saved Filters",
    description: "Saved filters pane title",
  },
});

const SavedFiltersDrawer: React.FC<Props> = ({
  editable,
  favoriteFiltersIds,
  filters,
  information,
  onDelete,
  onEdit,
  onHide,
  onSelect,
  showOverrideConfirmation,
  visible,
}) => {
  const intl = useIntl();
  const analyticsService = useAnalytics();

  const handleDelete = useCallback(
    (savedFilter) => {
      analyticsService?.clicked(["Delete Filter"], { filter: savedFilter });
      onDelete?.(savedFilter);
    },
    [analyticsService, onDelete]
  );

  return (
    <ListDrawerWithPagination<MapSavedFilter>
      deleteConfirmationText={intl.formatMessage(messages.deleteConfirmation)}
      icon={faSave}
      information={information}
      items={filters}
      onDelete={editable ? handleDelete : undefined}
      onEdit={editable ? onEdit : undefined}
      onHide={onHide}
      onSelect={onSelect}
      paneTitle={intl.formatMessage(messages.title)}
      rowDisabledTooltip={intl.formatMessage(messages.disabledTooltip, {
        b: (text) => <b>{text}</b>,
      })}
      rowSubTitleGetter={({ updatedAt, user }) => (
        <div className={styles.details}>
          {user && <Avatar className={styles.avatar} user={user} />}
          <div>
            {intl.formatMessage(messages.subTitle1, { updatedAt: formatDate(updatedAt, "PPp") })}
          </div>
        </div>
      )}
      rowTitleGetter={({ id, name }) => (
        <div>
          {name}
          {favoriteFiltersIds && favoriteFiltersIds.includes(id) ? (
            <FontAwesomeIcon className={styles.favoriteIcon} icon={faStar} size="xs" />
          ) : null}
        </div>
      )}
      selectConfirmationConfirmText={
        showOverrideConfirmation ? intl.formatMessage(messages.apply) : undefined
      }
      selectConfirmationText={
        showOverrideConfirmation ? intl.formatMessage(messages.selectConfirmation) : undefined
      }
      visible={visible}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  favoriteFiltersIds: getFavoriteFilters(state),
});

const mapDispatchToProps = {
  onDelete: deleteSavedFilter.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedFiltersDrawer);
